import React, { useEffect, useState } from 'react';
import ModalLayout from './ModalLayout';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../redux/modals';
import { deviceRunBuild } from '../../api/devices';

function FirmwareBuildModal({
  deviceId,
  previousLibraries,
  previousFqbn,
  closeModal,
}) {
  const dispatch = useDispatch();
  const [fqbn, setFqbn] = useState(previousFqbn || '');
  const [libraries, setLibraries] = useState(previousLibraries || '');
  const [error, setError] = useState('');

  const device = useSelector((state) =>
    state.device.devices.find((d) => d.id === deviceId)
  );
  const availableFqbn = useSelector((state) => state.device.availableFqbn);
  const defaultFqbn = useSelector((state) => state.device.defaultFqbn);

  const repoPattern =
    /^https:\/\/github\.com\/[a-zA-Z0-9_.-]+\/[a-zA-Z0-9_.-]+\.git(?:#[a-zA-Z0-9_.-]*)?$/;

  useEffect(() => {
    setFqbn(previousFqbn || defaultFqbn[device.platform]);
  }, [device]);

  const handleInputChange = (event) => {
    setLibraries(event.target.value);
    setError('');
  };

  const runBuild = () => {
    deviceRunBuild(deviceId, fqbn, libraries);
  };

  const validateLibraries = (input) => {
    if (!input) {
      return true;
    }

    const repoStrings = input.split(/\s+/);

    for (const repo of repoStrings) {
      if (repo && !repoPattern.test(repo)) {
        setError(
          `Invalid repository format: ${repo}. Expected format: https://github.com/owner/repo.git#directory`
        );
        return false;
      }
    }

    return true;
  };

  const handleRunBuild = () => {
    if (validateLibraries(libraries)) {
      runBuild(fqbn, libraries);
      closeModal();
      dispatch(openModal('firmwareInfoModal'));
    }
  };

  return (
    <ModalLayout closeModal={closeModal} className="!p-0 !bg-transparent">
      {/* Modal Container with emphasized border and shadow */}
      <div className="relative rounded-2xl bg-white p-6 shadow-2xl border-2 border-t-0 border-white/80 backdrop-blur-sm">
        {/* Title with subtle reflection effect */}
        <div className="flex items-center mb-4">
          <svg
            className="w-6 h-6 text-blue-500 mr-2"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z"
              clipRule="evenodd"
            />
          </svg>
          <h2 className="text-xl font-semibold text-blue-800/90 bg-white/30 px-3 py-1 rounded-lg backdrop-blur-sm">
            Компиляция прошивки в облаке
          </h2>
        </div>

        {/* Code Block - Terminal style */}
        <div className="mb-6 rounded-xl bg-blue-900/10 p-4 border border-white/50 shadow-inner">
          <div className="flex mb-2">
            <div className="w-3 h-3 rounded-full bg-red-400 mr-2"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-400 mr-2"></div>
            <div className="w-3 h-3 rounded-full bg-green-400"></div>
          </div>
          <pre className="whitespace-pre-wrap break-words font-mono text-sm text-blue-900">
            <code>
              <span className="text-blue-600 font-medium">
                arduino-cli compile
              </span>{' '}
              \<br />
              &nbsp;&nbsp;--fqbn{' '}
              <select
                value={fqbn}
                onChange={(e) => setFqbn(e.target.value)}
                className="w-[220px] border border-blue-200 bg-white/70 text-blue-900 px-3 py-1 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300/50"
              >
                {availableFqbn[device.platform].map((unit) => (
                  <option
                    key={unit.code}
                    value={unit.code}
                    className={`${unit.code === defaultFqbn[device.platform] ? 'bg-blue-100' : ''}`}
                  >
                    {unit.name}
                  </option>
                ))}
              </select>{' '}
              <span className="text-blue-400/80">({fqbn})</span>
              <br />
              &nbsp;&nbsp;--library ArduinoJson/src \<br />
              &nbsp;&nbsp;--library pubsubclient/src \<br />
              &nbsp;&nbsp;--library improvWifi/src \<br />
              &nbsp;&nbsp;
              <span className="text-blue-400">--library ...</span>
            </code>
          </pre>
        </div>

        {/* Libraries Input - Bubble textarea */}
        <div className="mb-6">
          <label className="block text-blue-700/90 mb-2 ml-1 text-sm">
            Ваши подключаемые библиотеки
          </label>
          <textarea
            value={libraries}
            onChange={handleInputChange}
            placeholder="Например: https://github.com/Arduino-IRremote/Arduino-IRremote.git#src&#10;Где #src - путь до исходных файлов библиотеки"
            className="w-full resize-y rounded-xl border border-blue-200/70 bg-white/90 p-3 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-300/50 text-blue-900 placeholder-blue-300"
            rows={4}
          />
        </div>

        {/* Error Message - Soft notification */}
        {error && (
          <div className="mb-4 rounded-xl bg-red-100/80 border border-red-200 p-3 shadow-inner">
            <div className="flex items-center text-red-700">
              <svg
                className="w-5 h-5 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex space-x-3">
          <button onClick={closeModal} className="btn-primary w-full">
            Отмена
          </button>
          <button
            onClick={handleRunBuild}
            className="btn-primary w-full bg-blue-200/50"
          >
            Запустить сборку
          </button>
        </div>
      </div>
    </ModalLayout>
  );
}

export default FirmwareBuildModal;
